import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import { fetchManufacturerList } from '#/api/imsMgmt/settings/manufacturer/fetchList';
import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { fetchMtrTemplateList } from '#/api/imsMgmt/settings/mtrTemplate/fetchList';
import { fetchMtrTemplateDetail } from '#/api/imsMgmt/settings/mtrTemplate/fetchDetail';
import { fetchMtrSpecDetail } from '#/api/imsMgmt/settings/mtrSpec/fetchDetail';
import {
  buildSpecEntriesFromTemplate,
  buildSpecSelectOptionsSeed,
  formatOriginalSerialListText,
  parseDetailSpecValues,
  parseOriginalSerialListText,
  serializeSpecValues,
} from '#/modules/imsMgmt/settings/mtr/helper/specValuesSerialize';
import {
  parseDetailAssemblyOptions,
  serializeAssemblyOptions,
} from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import {
  buildUnitAggregatePayload,
  mapDetailUnitToForm,
  serializeUnitConvert,
} from '#/modules/imsMgmt/settings/mtr/helper/unitAggregateSerialize';
import { MTR_FORM_DEFAULT_VALUES } from '#/modules/imsMgmt/settings/mtr/schemas/mtrSchema';
import {
  fromTree,
  resolveMainIdBySubId,
} from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import {
  manufacturerListQueryKey,
  mtrSpecDetailQueryKey,
  mtrStructTreeQueryKey,
  mtrTemplateDetailQueryKey,
  mtrTemplateListQueryKey,
} from '#/utils/queryKeys';

const LOOKUP_LIST_QUERY = {
  page: 1,
  pageSize: 200,
  keyword: '',
  sort: [],
  filters: { status: 'ACTIVE' },
};

export function mapMtrDetailToForm(detail, treeRows = []) {
  if (!detail) {
    return MTR_FORM_DEFAULT_VALUES;
  }

  const model = fromTree(treeRows);
  const mtrStructSubId = detail.mtrStructSub?.id ?? detail.mtrStructSubId ?? '';
  const mtrStructMainId = resolveMainIdBySubId(model, mtrStructSubId);
  const { bySpecId, specCustom } = parseDetailSpecValues(detail);
  const unitForm = mapDetailUnitToForm(detail);

  return {
    mtrKind: detail.mtrKind ?? 'NORMAL',
    mtrStructMainId,
    mtrStructSubId,
    mtrTemplateId: detail.mtrTemplate?.id ?? detail.mtrTemplateId ?? '',
    manufacturerId: detail.manufacturer?.id ?? detail.manufacturerId ?? '',
    unitBaseId: unitForm.unitBaseId,
    baseUnitCode: '',
    baseUnitName: unitForm.baseUnitName,
    unitConvert: unitForm.unitConvert,
    mtrNo: detail.mtrNo ?? '',
    name: detail.name ?? '',
    sortKey: detail.sortKey ?? '',
    remark: detail.remark ?? '',
    safetyQty: detail.safetyQty ?? '',
    baseUnitPrice: detail.baseUnitPrice ?? '',
    receiptDefaultQty: detail.receiptDefaultQty ?? '',
    defaultDisplayUnit: detail.mtrKind === 'GROUP'
      ? ''
      : (detail.defaultDisplayUnit ?? 'BASE'),
    originalSerialListText: formatOriginalSerialListText(detail.originalSerialList),
    specEntries: [],
    specCustom,
    assemblyOptions: parseDetailAssemblyOptions(detail),
    bomId: detail.bom?.id ?? detail.bomId ?? '',
    bomCode: detail.bom?.code ?? '',
    bomName: detail.bom?.name ?? '',
    bomVersion: detail.bom?.version ?? '',
    _specBySpecId: bySpecId,
    _baseUnitInitialized: unitForm._baseUnitInitialized,
  };
}

export function buildMtrMutationPayload(values, updatedAt = null) {
  const isGroup = values.mtrKind === 'GROUP';
  const specEntries = values.specEntries ?? [];
  const hasTemplate = Boolean(values.mtrTemplateId);
  // 已選模板但 entries 尚未 hydrate：勿送 specValues，避免 PATCH 整包清空模板規格。
  // 自訂規格仍走既有 serializeSpecValues；僅在省略整個欄位時一併暫緩。
  const shouldOmitSpecValues = hasTemplate && specEntries.length === 0;

  const payload = {
    mtrKind: values.mtrKind ?? 'NORMAL',
    mtrStructSubId: values.mtrStructSubId,
    mtrTemplateId: values.mtrTemplateId || null,
    manufacturerId: isGroup ? null : (values.manufacturerId || null),
    mtrNo: values.mtrNo?.trim() || null,
    name: values.name.trim(),
    // 表單一律帶出：空白 → null（create 存空、PATCH 清除）
    sortKey: values.sortKey?.trim() || null,
    remark: values.remark?.trim() || null,
    safetyQty: values.safetyQty === '' || values.safetyQty == null
      ? null
      : Number(values.safetyQty),
    baseUnitPrice: values.baseUnitPrice === '' || values.baseUnitPrice == null
      ? null
      : Number(values.baseUnitPrice),
    originalSerialList: parseOriginalSerialListText(values.originalSerialListText),
  };

  if (!isGroup) {
    const unit = buildUnitAggregatePayload(values);
    if (unit !== undefined) {
      payload.unit = unit;
    }

    const receiptQtyRaw = values.receiptDefaultQty;
    payload.receiptDefaultQty = receiptQtyRaw === '' || receiptQtyRaw == null
      ? 0
      : Number(receiptQtyRaw);

    // 無進位時不可送 CONVERT；同筆 PATCH 改為 BASE
    const hasConvert = Boolean(serializeUnitConvert(values.unitConvert));
    const displayUnit = values.defaultDisplayUnit === 'CONVERT' && hasConvert
      ? 'CONVERT'
      : 'BASE';
    payload.defaultDisplayUnit = displayUnit;
  } else {
    payload.defaultDisplayUnit = null;
  }

  if (!shouldOmitSpecValues) {
    payload.specValues = serializeSpecValues(specEntries, values.specCustom);
  }

  if (isGroup) {
    payload.assemblyOptions = serializeAssemblyOptions(values.assemblyOptions);
  }

  // 表單一律帶出：空字串 → null（解除／未綁）；有 id → 換綁／綁定。
  // PATCH「未傳＝保留」僅在故意省略欄位時；本表單以顯式 bomId 為準。
  payload.bomId = values.bomId?.trim() ? values.bomId.trim() : null;

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

function useMtrForm({
  mtrStructMainId = '',
  mtrStructSubId = '',
  mtrTemplateId = '',
  seedSpecOptions = [],
}) {
  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const manufacturerQuery = useQuery({
    queryKey: manufacturerListQueryKey(LOOKUP_LIST_QUERY),
    queryFn: () => fetchManufacturerList(LOOKUP_LIST_QUERY),
  });

  const templateListQuery = useQuery({
    queryKey: mtrTemplateListQueryKey({
      ...LOOKUP_LIST_QUERY,
      filters: { ...LOOKUP_LIST_QUERY.filters, mtrStructSubId },
    }),
    queryFn: () => fetchMtrTemplateList({
      ...LOOKUP_LIST_QUERY,
      filters: { ...LOOKUP_LIST_QUERY.filters, mtrStructSubId },
    }),
    enabled: Boolean(mtrStructSubId),
  });

  const templateDetailQuery = useQuery({
    queryKey: mtrTemplateDetailQueryKey(mtrTemplateId),
    queryFn: () => fetchMtrTemplateDetail(mtrTemplateId),
    enabled: Boolean(mtrTemplateId),
  });

  const treeRows = useMemo(
    () => treeQuery.data?.data ?? [],
    [treeQuery.data?.data],
  );

  const structMainOptions = useMemo(() => fromTree(treeRows).mains, [treeRows]);

  const structSubOptions = useMemo(() => {
    if (!mtrStructMainId) {
      return [];
    }

    const model = fromTree(treeRows);
    const group = model.subGroups.find((item) => item.mainId === mtrStructMainId);

    return (group?.subs ?? []).map((sub) => ({
      value: sub.value,
      label: sub.label,
    }));
  }, [treeRows, mtrStructMainId]);

  const manufacturerOptions = useMemo(() => (
    (manufacturerQuery.data?.data ?? []).map((item) => ({
      value: item.id,
      label: item.code ? `${item.code} - ${item.name}` : item.name,
    }))
  ), [manufacturerQuery.data?.data]);

  const templateOptions = useMemo(() => (
    (templateListQuery.data?.data ?? []).map((item) => ({
      value: item.id,
      label: item.code ? `${item.code} - ${item.name}` : item.name,
    }))
  ), [templateListQuery.data?.data]);

  const selectSpecIds = useMemo(() => {
    const specs = templateDetailQuery.data?.data?.specs ?? [];
    return specs
      .filter((rule) => (rule.mtrSpec?.specType ?? '') === 'SELECT')
      .map((rule) => rule.mtrSpec?.id ?? rule.mtrSpecId)
      .filter(Boolean);
  }, [templateDetailQuery.data?.data?.specs]);

  const specDetailQueries = useQueries({
    queries: selectSpecIds.map((specId) => ({
      queryKey: mtrSpecDetailQueryKey(specId),
      queryFn: () => fetchMtrSpecDetail(specId),
      enabled: Boolean(specId),
    })),
  });

  const specSelectOptionsMap = useMemo(() => {
    const seeded = buildSpecSelectOptionsSeed(seedSpecOptions);
    const map = { ...seeded };

    selectSpecIds.forEach((specId, index) => {
      const detail = specDetailQueries[index]?.data?.data;
      const fromApi = (detail?.options ?? []).map((option) => ({
        value: option.id,
        label: option.code ? `${option.code} - ${option.name}` : option.name,
      }));
      const seedChoices = seeded[specId] ?? [];

      if (fromApi.length === 0) {
        map[specId] = seedChoices;
        return;
      }

      const merged = [...fromApi];
      seedChoices.forEach((choice) => {
        if (!merged.some((option) => String(option.value) === String(choice.value))) {
          merged.push(choice);
        }
      });
      map[specId] = merged;
    });

    return map;
  }, [selectSpecIds, specDetailQueries, seedSpecOptions]);

  const specDetailsById = useMemo(() => {
    const map = {};

    selectSpecIds.forEach((specId, index) => {
      const detail = specDetailQueries[index]?.data?.data;
      if (detail) {
        map[specId] = detail;
      }
    });

    return map;
  }, [selectSpecIds, specDetailQueries]);

  const manufacturers = useMemo(
    () => manufacturerQuery.data?.data ?? [],
    [manufacturerQuery.data?.data],
  );

  const templateRules = templateDetailQuery.data?.data?.specs ?? [];

  const isBootstrapLoading = treeQuery.isLoading
    || manufacturerQuery.isLoading;

  const isTemplateListLoading = Boolean(mtrStructSubId) && templateListQuery.isLoading;

  const isSpecHydrationLoading = Boolean(mtrTemplateId) && (
    templateDetailQuery.isLoading
    || specDetailQueries.some((query) => query.isLoading)
  );

  return {
    treeRows,
    structMainOptions,
    structSubOptions,
    manufacturerOptions,
    manufacturers,
    templateOptions,
    templateRules,
    templateDetail: templateDetailQuery.data?.data,
    specSelectOptionsMap,
    specDetailsById,
    isBootstrapLoading,
    isTemplateListLoading,
    isSpecHydrationLoading,
    buildSpecEntriesFromTemplate,
    refetchStructTree: treeQuery.refetch,
    refetchManufacturers: manufacturerQuery.refetch,
    refetchTemplateList: templateListQuery.refetch,
    isStructTreeRefreshing: treeQuery.isFetching,
    isManufacturersRefreshing: manufacturerQuery.isFetching,
    isTemplateListRefreshing: templateListQuery.isFetching,
  };
}

export default useMtrForm;
